import type { fabric } from 'fabric';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ColorField from './shared/color-field';
import { registerInspector } from './inspector-registry';
import MixedSelectField from './shared/mixed-select-field';
import NumberField from './shared/number-field';
import PlaceholderTransformSection from './placeholder-transform-section';
import CornerRadiusSection from './shared/corner-radius-section';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import PlaceholderIdentitySection from './shared/placeholder-identity-section';
import { useCommonFabricData } from './shared/use-common-fabric-data';
import { imagePlaceholderCornerStrategy } from './shared/corner-radii-strategies';
import { useCornerRadii } from './shared/use-corner-radii';
import type { ObjectInspectorProps } from './types';

const MIXED = '(разное)';

const FIT_MODES = [
  { value: 'contain', label: 'Вписать' },
  { value: 'cover', label: 'Заполнение' },
];

const PlaceholderImageInspector: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const fitMode = useCommonFabricData<string>(objects, 'fitMode', editor);
  const previewMode = useCommonFabricData<boolean>(objects, 'previewMode', editor);
  const stroke = useCommonFabricData<string>(objects, 'stroke', editor);
  const strokeWidth = useCommonFabricData<number>(objects, 'strokeWidth', editor);
  const opacity = useCommonFabricData<number>(objects, 'opacity', editor);
  const cornerStrategy = useMemo(() => imagePlaceholderCornerStrategy(editor), [editor]);
  const radii = useCornerRadii(objects, editor, cornerStrategy);

  const refitAll = useCallback(() => {
    for (const o of objects) editor.placeholders?.refit(o as fabric.Group);
  }, [editor, objects]);

  const commit = useCallback(
    <T,>(setter: (value: T | undefined) => void) =>
      (value: T | undefined) => {
        setter(value);
        refitAll();
      },
    [refitAll],
  );

  const previewRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (previewRef.current) previewRef.current.indeterminate = previewMode.isMixed;
  }, [previewMode.isMixed, previewMode.value]);

  const handlePreviewToggle = () => {
    const next = previewMode.isMixed ? true : !(previewMode.value ?? false);
    previewMode.setValue(next || undefined);
    refitAll();
  };

  return (
    <>
      <InspectorSection title="Предпросмотр">
        <InspectorRow label="Режим предпросмотра">
          <CheckboxLabel>
            <input
              ref={previewRef}
              type="checkbox"
              checked={Boolean(previewMode.value) && !previewMode.isMixed}
              onChange={handlePreviewToggle}
            />
            <span>{previewMode.isMixed ? '(разное)' : 'показать как замещённое изображение'}</span>
          </CheckboxLabel>
        </InspectorRow>
      </InspectorSection>
      <PlaceholderIdentitySection objects={objects} editor={editor} />
      <InspectorSection title="Изображение">
        <InspectorRow label="Режим вписывания">
          <MixedSelectField
            value={fitMode.value}
            isMixed={fitMode.isMixed}
            onChange={commit<string>(fitMode.setValue)}
            options={FIT_MODES}
            fallback="contain"
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Внешний вид">
        <InspectorRow label="Обводка">
          <ColorField
            value={stroke.isMixed ? undefined : stroke.value}
            onChange={commit<string>(stroke.setValue)}
            placeholder={stroke.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Толщина">
          <NumberField
            value={strokeWidth.isMixed ? undefined : strokeWidth.value}
            onChange={commit<number>(strokeWidth.setValue)}
            min={0}
            placeholder={strokeWidth.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Прозрачность">
          <NumberField
            value={opacity.isMixed ? undefined : opacity.value}
            onChange={commit<number>(opacity.setValue)}
            step={0.05}
            min={0}
            max={1}
            placeholder={opacity.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <CornerRadiusSection control={radii} />
      <PlaceholderTransformSection objects={objects} editor={editor} />
    </>
  );
};

export default PlaceholderImageInspector;

registerInspector('placeholder-image', PlaceholderImageInspector);

const CheckboxLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label
    style={{
      display: 'inline-flex',
      gap: 6,
      alignItems: 'center',
      fontSize: 12,
      color: '#374151',
      cursor: 'pointer',
    }}
  >
    {children}
  </label>
);
